<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'WEmployeeModal',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    parent: {
      type: Object,
      required: true,
    },
    item: {
      type: Object,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const resource = ref(null);
  const editor = ref(null);
  const createTitle = ref($fn.value.pgettext('Employeees', 'Add employee'));
  const editTitle = ref($fn.value.pgettext('Employeees', 'Edit employee'));
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'md',
  }));

  const $data = computed(() => ({
    resource: resource.value,
    editor: editor.value,
    createTitle: createTitle.value,
    editTitle: editTitle.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_resource = () => {
    return props.item || $fn.value.schemaDefaults('CreateEmployee');
  };
  const $$load_editor = () => {
    return resource.value.editor();
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'resource' },
      { name: 'editor', dependsOn: ['resource'] },
      { name: 'createTitle' },
      { name: 'editTitle' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: { resource: $$load_resource, editor: $$load_editor },
    ux: {},
    dataRefs: {
      resource,
      editor,
      createTitle,
      editTitle,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    if (loaded.value) {
      return {
        title: resource.value.id == null ? createTitle.value : editTitle.value,
      };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.parent,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('parent');
    }
  );
  watch(
    () => props.item,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('item');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_wiutpg4ny_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_closeComponent_8r51pbn8u_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        value: $event.data.response.data,
      },
    };
  };
  const act_1040167445267876_closeComponent_ii8sc0izc_getActionArgs = async (
    $event
  ) => {
    return {
      result: {},
    };
  };
  const act_1040167445267876_rest_49lner6lx_getActionArgs = async ($event) => {
    return {
      method: 'save',
      container: props.parent.employees,
      resource: editor.value,
    };
  };

  const act_7315092382398562_closeModal_wiutpg4ny =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_wiutpg4ny_getActionArgs,
    });
  const inst_evh_7315092382398562_close_dkj5qfs2m =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_wiutpg4ny,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_dkj5qfs2m],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_bqw257tk4 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_at4skd334 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_bqw257tk4,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_at4skd334],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_ve508apex =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_tl8q975ww =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_ve508apex,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_tl8q975ww],
    event: 'close',
  });
  const act_1040167445267876_closeComponent_8r51pbn8u =
    new actionTypes.CloseComponentAction({
      actionArgs: act_1040167445267876_closeComponent_8r51pbn8u_getActionArgs,
    });
  const inst_evh_1040167445267876_saved_e8pgeksfy =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeComponent_8r51pbn8u,
      event: 'saved',
      displayName: 'closeComponent',
    });
  const act_1040167445267876_closeComponent_ii8sc0izc =
    new actionTypes.CloseComponentAction({
      actionArgs: act_1040167445267876_closeComponent_ii8sc0izc_getActionArgs,
    });
  const inst_evh_1040167445267876_deleted_ww1l393az =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeComponent_ii8sc0izc,
      event: 'deleted',
      displayName: 'closeComponent',
    });
  const act_1040167445267876_rest_49lner6lx = new actionTypes.RestAction({
    actionArgs: act_1040167445267876_rest_49lner6lx_getActionArgs,
    displayName: 'submit',
    events: [
      inst_evh_1040167445267876_saved_e8pgeksfy,
      inst_evh_1040167445267876_deleted_ww1l393az,
    ],
  });
  const inst_evh_1040167445267876_submit_oqoa1udo2 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_rest_49lner6lx,
      event: 'submit',
      displayName: 'submit',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_oqoa1udo2],
    event: 'submit',
  });
  const act_2434514588044777_closeComponent_9icqhrcze =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2434514588044777_clickPreventStop_kq9jmqh5l =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeComponent_9icqhrcze,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_kq9jmqh5l],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_dkj5qfs2m,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_at4skd334,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_tl8q975ww,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_saved_e8pgeksfy,
    inst_evh_1040167445267876_deleted_ww1l393az,
    inst_evh_1040167445267876_submit_oqoa1udo2,
    submit: inst_evh_1040167445267876_submit_oqoa1udo2,
    inst_evh_1040167445267876_submit,
    inst_evh_2434514588044777_clickPreventStop_kq9jmqh5l,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_dkj5qfs2m = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_dkj5qfs2m.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_at4skd334 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_at4skd334.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_tl8q975ww = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_tl8q975ww.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_saved_e8pgeksfy = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_saved_e8pgeksfy.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_deleted_ww1l393az = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_deleted_ww1l393az.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_oqoa1udo2 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_oqoa1udo2.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_kq9jmqh5l = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_kq9jmqh5l.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="WEmployeeModal"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        name="employee"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <UcEmployeeFieldset :employee="editor"> </UcEmployeeFieldset>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :form="forms.employee"
          :text="gettext('Save')"
          type="submit"
          :spinning="actions.submit.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Close')"
          variant="default"
          :disabled="actions.submit.isRunning"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
